import type { Expense, Transport, TripConfig } from '../types';
import { stripUndef } from './dayWrites';
import { expenseBaseCurrency } from './expenseUtils';
import { chargeToLog, costKindForPaymentStatus, paymentStatusForCostKind } from './bookingCostReview';

export type BookingCost = Pick<Transport, 'id' | 'costAmount' | 'costCurrency' | 'costKind' | 'paidStatus' | 'depositAmount' | 'depositCurrency'> & {
  _logToExpenses?: boolean; _paidBy?: string; _costSplit?: string[]; _expenseNote?: string;
};

/** Prepare once outside a retryable transaction; money stays owned by Expenses after linking. */
export async function prepareBookingExpense(trip: TripConfig, next: BookingCost, userId: string,
  metadata: Pick<Expense, 'desc' | 'cat' | 'catName' | 'date' | 'sourceType'>,
  rateFor: (from: string, to: string, date?: string) => Promise<number>): Promise<Expense | undefined> {
  if (!next._logToExpenses) return;
  if (!next.id || !Number.isFinite(next.costAmount) || !(next.costAmount! > 0)) throw new Error('Enter a valid cost before logging this booking.');
  const active = (trip.travellers || []).filter(t => t.inviteStatus !== 'pending').map(t => t.id);
  const paidBy = next._paidBy || userId;
  const split = [...new Set(next._costSplit?.length ? next._costSplit : active)];
  if (!active.includes(paidBy) || !split.length || split.some(id => !active.includes(id))) {
    throw new Error('Choose a payer and split from travellers who have joined the trip.');
  }
  const targetCurrency = expenseBaseCurrency(trip);
  const kind = next.costKind || costKindForPaymentStatus(next.paidStatus);
  const charge = chargeToLog(kind, next.costAmount!, next.costCurrency || targetCurrency,
    next.depositAmount, next.depositCurrency);
  if (!charge) throw new Error('Confirm an agreed cost and payment status before logging an expense; quotes and holds are not charges.');
  const fullDate = new Date().toISOString();
  const rate = await rateFor(charge.currency, targetCurrency, fullDate);
  const targetAmount = Math.round(charge.amount * rate * 100) / 100;
  if (!Number.isFinite(targetAmount) || targetAmount <= 0) throw new Error('Could not calculate this cost. Please try again.');
  return stripUndef({
    ...metadata, id: `${metadata.sourceType}-${next.id}`, sourceId: next.id,
    amount: charge.amount, currency: charge.currency, targetAmount, targetCurrency,
    paid_by: paidBy, split, fullDate, amountBasis: 'full-cost', paidStatus: paymentStatusForCostKind(kind),
    ...(kind === 'deposit' ? { depositAmount: next.depositAmount,
      depositCurrency: (next.depositCurrency || charge.currency).trim().toUpperCase() } : {}),
    ...(next._expenseNote?.trim() ? { note: next._expenseNote.trim() } : {}),
  });
}
